import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Space,
  Spin,
  Table,
  notification,
  Tag,
  Typography,
  Progress,
  Dropdown,
  Modal,
  Button,
  Tooltip,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import moment from "moment";
import { FinetuneRunNew } from "../../../model_gen/finetuning";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import Card from "antd/es/card/Card";
import {
  PlusOutlined,
  MoreOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { PrimaryButton } from "../../shared/button/Buttons";
import { FinetuningService } from "../../../services/FinetuningService";
type FinetuneRunColumns = ColumnsType<FinetuneRunNew>;

const FinetuneRuns: React.FC = () => {
  const { apiKey } = usePermissions();
  const [finetuneRuns, setFinetuneRuns] = useState<FinetuneRunNew[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRuns, setTotalRuns] = useState(0);
  const pageSize = 10;

  const navigate = useNavigate();

  const getStatusTagColor = (status: string): string => {
    switch (status.toUpperCase()) {
      case "CREATED":
        return "default";
      case "SCHEDULED":
        return "processing";
      case "RUNNING":
        return "processing";
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      case "CANCELLED":
        return "warning";
      default:
        return "default";
    }
  };

  const handleDeleteRun = (run: FinetuneRunNew) => {
    Modal.confirm({
      title: "Delete Finetune Run",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${run.display_name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        FinetuningService.updateFinetuneStatus(
          run.id.toString(),
          "DELETED",
          setIsLoading,
          apiKey
        ).subscribe(
          () => {
            notification.success({
              message: "Success",
              description: "Finetune run deleted successfully",
            });
            refreshFinetuneRuns();
          },
          (error: any) => {
            console.error(error);
            notification.error({
              message: "Error",
              description: "Failed to delete finetune run",
            });
          }
        );
      },
    });
  };

  const finetuneRunColumns: FinetuneRunColumns = [
    {
      title: "Name",
      key: "name",
      width: 300,
      render: (_, record) => {
        return record.display_name;
      },
      sorter: (a, b) => {
        const aName = a.display_name as string;
        const bName = b.display_name as string;
        return aName.localeCompare(bName);
      },
    },
    {
      title: "Base Model",
      key: "base_model",
      dataIndex: "base_model",
      render: (baseModel) => {
        return baseModel.replace("unsloth/", "").replace("Meta-", "");
      },
    },
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      render: (date) => moment(date).format("YYYY-MM-DD hh:mm:ss A"),
      sorter: (a, b) =>
        moment(a.date_created).unix() - moment(b.date_created).unix(),
    },
    {
      title: "Data Source",
      key: "data_source",
      render: (_, record) => {
        if (record.intent_group_name) {
          return record.intent_group_name;
        } else if (record.application_name && record.intent_name) {
          return `${record.application_name} / ${record.intent_name}`;
        } else {
          return "";
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const statusUpper = status.toUpperCase();

        // Calculate estimated time remaining for RUNNING status
        const getTimeRemaining = () => {
          if (
            statusUpper === "RUNNING" &&
            record.progress &&
            record.date_created
          ) {
            const startTime = moment(record.date_created);
            const now = moment();
            const elapsedMinutes = now.diff(startTime, "minutes");

            if (record.progress > 0 && elapsedMinutes > 0) {
              // Calculate total time based on progress so far
              const totalEstimatedMinutes = elapsedMinutes / record.progress;
              const remainingMinutes = totalEstimatedMinutes - elapsedMinutes;

              if (remainingMinutes <= 0) {
                return "Almost complete";
              }

              if (remainingMinutes < 60) {
                return `${Math.ceil(remainingMinutes)} minutes remaining`;
              } else {
                const hours = Math.floor(remainingMinutes / 60);
                const mins = Math.ceil(remainingMinutes % 60);
                return `${hours}h ${mins}m remaining`;
              }
            }
            return "Calculating...";
          }
          return null;
        };

        if (statusUpper === "RUNNING") {
          const percent = record.progress
            ? Math.round(record.progress * 100)
            : 0;
          return (
            <div style={{ width: "100px" }}>
              <Tooltip title={getTimeRemaining()}>
                <Progress
                  percent={Number(percent.toFixed(1))}
                  size="small"
                  status="active"
                />
              </Tooltip>
            </div>
          );
        } else if (statusUpper === "COMPLETED") {
          return (
            <div style={{ width: "100px" }}>
              <Progress percent={100} size="small" />
            </div>
          );
        } else {
          return <Tag color={getStatusTagColor(status)}>{statusUpper}</Tag>;
        }
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "",
      key: "actions",
      width: "48px",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleDeleteRun(record);
                },
              },
            ],
          }}
          trigger={["click"]}>
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    // Initial data fetch - show loading indicator
    refreshFinetuneRuns(true);

    // Create a function to check if there are running jobs
    const hasRunningJobs = () => {
      return finetuneRuns.some(
        (run) =>
          run.status.toUpperCase() === "RUNNING" ||
          run.status.toUpperCase() === "SCHEDULED"
      );
    };

    // Set up polling interval (60 seconds)
    const intervalId = setInterval(() => {
      // Only fetch if there are running jobs or on initial load
      // Don't show loading indicator for background polling
      if (hasRunningJobs() || finetuneRuns.length === 0) {
        refreshFinetuneRuns(false);
      }
    }, 60000); // 60 seconds

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [apiKey, currentPage]); // Remove finetuneRuns from dependencies

  const refreshFinetuneRuns = (showLoading = true) => {
    const offset = (currentPage - 1) * pageSize;
    FinetuneRunService.getFinetuneRuns(
      offset,
      pageSize,
      showLoading ? setIsLoading : null,
      apiKey
    ).subscribe(
      (response: { items: FinetuneRunNew[]; total: number }) => {
        setFinetuneRuns(response.items);
        setTotalRuns(response.total);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: "Failed to load finetune runs",
        });
      }
    );
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setCurrentPage(pagination.current);
    }
  };

  return (
    <Card className="finetune-runs-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}>
          <Col>
            <Typography style={{ fontSize: "24px", fontWeight: "600" }}>
              Finetune Runs
            </Typography>
          </Col>
          <Col>
            <PrimaryButton
              title="New Finetune Run"
              className="buttonWrapper"
              icon={<PlusOutlined />}
              onClick={() => navigate("/models/finetuning/new")}
            />
          </Col>
        </Row>
        {isLoading ? (
          <Spin
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px",
            }}
          />
        ) : (
          <Table
            rowKey={(record) => record.id.toString()}
            columns={finetuneRunColumns}
            dataSource={finetuneRuns}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalRuns,
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
            onRow={(record) => {
              return {
                onClick: () => navigate(`/models/finetuning/${record.id}`),
              };
            }}
          />
        )}
      </Space>
    </Card>
  );
};

export default FinetuneRuns;
